import { useMemo, useState } from 'react'
import clsx from 'clsx'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../../../../../assets/svgIcons/LoaderAdm.json'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { formatDate } from '../../../../../../../components/utils/formatter'
import { calcAmountsForRow } from '../../utils/dteAmounts'
import { formatCLP } from '../../utils/formatCLP'
import EstadoTrazaModal from './EstadoTrazaModal'
import { getDteColor } from '../../utils/dteColors'
import { getDteAbbreviation } from '../../utils/dteLabels'
import iconEnlace from '../../../../../../../assets/iconos/editDoc.png'
import coparDoc from '../../../../../../../assets/iconos/coparDoc.png'

export default function Table({
  rows,
  className,
  lastElementRef,
  hasNextPage,
  isFetchingNextPage,
  docType = '',
  detailsByKey = {},
  loadingActionByKey = {},
  getRowKey,
  onOpenPdf,
  onOpenXml,
  onEmitNC,
  onCloneDoc
}) {
  const [order, setOrder] = useState({
    col: 'Fecha',
    field: 'date_issued',
    direction: 'desc'
  })

  const [stateModalOpen, setStateModalOpen] = useState(false)
  const [selectedRowState, setSelectedRowState] = useState(null)

  const handleOpenStateModal = row => {
    setSelectedRowState(row)
    setStateModalOpen(true)
  }
  //comentadooooooooo
  const handleCloseStateModal = () => {
    setStateModalOpen(false)
    setSelectedRowState(null)
  }

  const colsTodos = useMemo(
    () => [
      { col: 'Folio', field: 'folio' },
      { col: 'Fecha', field: 'date_issued' },
      { col: 'Estado', field: 'direction_label' },
      { col: 'Tipo', field: 'dte_type_description' },
      { col: 'Emisor', field: 'sender_name' },
      { col: 'Receptor', field: 'receiver_name' },
      { col: 'Monto', field: 'amount_with_iva' },
      // { col: 'Estado', field: 'estado_ui' },
      { col: 'Acciones', field: 'acciones' }
    ],
    []
  )

  const colsEmitidos = useMemo(
    () => [
      { col: 'Folio', field: 'folio' },
      { col: 'Fecha', field: 'date_issued' },
      { col: 'Estado', field: 'direction_label' },
      { col: 'Tipo', field: 'dte_type_description' },
      { col: 'Cliente', field: 'receiver_name' },
      { col: 'Monto', field: 'amount_with_iva' },
      // { col: 'Estado', field: 'estado_ui' },
      { col: 'Acciones', field: 'acciones' }
    ],
    []
  )

  const colsRecibidos = useMemo(
    () => [
      { col: 'Folio', field: 'folio' },
      { col: 'Fecha', field: 'date_issued' },
      { col: 'Estado', field: 'direction_label' },
      { col: 'Tipo', field: 'dte_type_description' },
      { col: 'Emisor', field: 'sender_name' },
      { col: 'Monto', field: 'amount_with_iva' },
      // { col: 'Estado', field: 'estado_ui' },
      { col: 'Acciones', field: 'acciones' }
    ],
    []
  )

  const columns = useMemo(() => {
    if (!docType) return colsTodos
    if (docType === 'issued') return colsEmitidos
    return colsRecibidos // received
  }, [docType, colsTodos, colsEmitidos, colsRecibidos])

  const handleSort = ({ field }) => {
    if (field === 'archivo') return
    const isAsc = order.field === field && order.direction === 'asc'
    setOrder(prev => ({
      ...prev,
      field,
      direction: isAsc ? 'desc' : 'asc'
    }))
  }

  const rowsWithEstado = useMemo(
    () =>
      (rows || []).map(r => {
        let label = 'Ver traza'
        if (r.document_states && r.document_states.length > 0) {
          label = r.document_states[0].label || 'Ver traza'
        }
        return {
          ...r,
          estado_ui: label,
          direction_label: r.__document_type === 'issued' ? 'Enviado' : 'Recibido',
          dte_type_description: r.dte_type_description || r.dte_type?.description || '-'
        }
      }),
    [rows]
  )

  const sortedRows = useMemo(() => {
    const list = [...rowsWithEstado]
    const field = order.field

    return list.sort((a, b) => {
      if (field === 'archivo') return 0

      const aVal = a?.[field]
      const bVal = b?.[field]

      if (field === 'amount_with_iva') {
        const na = Number(aVal ?? 0)
        const nb = Number(bVal ?? 0)
        return order.direction === 'asc' ? na - nb : nb - na
      }

      if (field === 'date_issued') {
        const da = aVal ? new Date(aVal).getTime() : 0
        const db = bVal ? new Date(bVal).getTime() : 0

        if (da === db) {
          const ta = a?.created_at ? new Date(a.created_at).getTime() : 0
          const tb = b?.created_at ? new Date(b.created_at).getTime() : 0
          return order.direction === 'asc' ? ta - tb : tb - ta
        }

        return order.direction === 'asc' ? da - db : db - da
      }

      const sa = String(aVal ?? '').toLowerCase()
      const sb = String(bVal ?? '').toLowerCase()
      if (sa > sb) return order.direction === 'asc' ? 1 : -1
      if (sa < sb) return order.direction === 'asc' ? -1 : 1
      return 0
    })
  }, [rowsWithEstado, order.field, order.direction])

  if (!rows || rows.length === 0) {
    return <div className='py-12 text-center text-xs'>No hay datos</div>
  }

  const Spinner = () => (
    <span className='inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin' />
  )

  const styles = {
    table:
      'w-full min-w-[980px] table-auto text-[0.65rem] sm:text-[0.725rem] border-collapse leading-tight font-sans',
    thead: 'sticky top-0 z-10 bg-white',
    th: 'py-2 px-1 sm:px-2 w-auto max-w-[120px] text-center break-words border-b border-gray-200 uppercase text-[0.55rem] sm:text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-100 hover:bg-gray-50 transition-colors',
    td: 'py-2 px-1 sm:px-2 w-auto max-w-[200px] text-center break-words align-middle',
    boldText: 'font-bold text-[0.62rem] sm:text-[0.65rem]',
    text: 'text-[0.65rem] sm:text-[0.705rem] text-gray-400 font-medium'
  }

  return (
    <div className={`overflow-auto rounded font-sans ${className}`}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            {columns.map((colObj, idx) => {
              return (
                <th
                  key={idx}
                  onClick={() => handleSort(colObj)}
                  className={clsx(styles.th, colObj.field !== 'archivo' ? 'cursor-pointer' : '')}
                >
                  <div className='flex items-center justify-center gap-1'>
                    <span>{colObj.col}</span>
                    {colObj.field !== 'archivo' && (
                      <KeyboardArrowDownIcon
                        fontSize='small'
                        className={clsx('transition-transform', {
                          'opacity-30': order.field !== colObj.field,
                          'rotate-180': order.field === colObj.field && order.direction === 'desc'
                        })}
                      />
                    )}
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {sortedRows.map((row, idx) => (
            <tr key={row.id ?? idx} className={styles.tr}>
              {columns.map((colObj, cIdx) => {
                const { field } = colObj

                if (field === 'folio') {
                  const key = getRowKey?.(row)
                  const detail = key ? detailsByKey?.[key] : null
                  const activeActions = key ? loadingActionByKey?.[key] : null
                  const isDocLoading = detail?.status === 'loading'
                  const showPdfSpinner = (isDocLoading || activeActions?.pdf) && activeActions?.pdf
                  const disablePdf = (isDocLoading && activeActions?.pdf) || !!activeActions?.pdf

                  return (
                    <td key={cIdx} className={styles.td}>
                      {row.folio ? (
                        <button
                          type='button'
                          onClick={() => onOpenPdf?.(row)}
                          disabled={disablePdf}
                          className={clsx(
                            styles.boldText,
                            'underline decoration-dotted underline-offset-2 hover:text-red-600 transition-colors',
                            { 'opacity-40 cursor-not-allowed': disablePdf }
                          )}
                          title='Ver PDF'
                        >
                          {showPdfSpinner ? <Spinner /> : row.folio}
                        </button>
                      ) : (
                        <span className={styles.boldText}>-</span>
                      )}
                    </td>
                  )
                }

                if (field === 'dte_type_description') {
                  const code = row?.dte_type_code ?? row?.dte_type?.code
                  const colorClasses = getDteColor(code)
                  const stateLabel = row?.document_states?.[0]?.label

                  return (
                    <td key={cIdx} className={styles.td}>
                      <span
                        className={clsx(
                          'px-2 py-1 rounded-full text-[9px] font-bold border',
                          colorClasses
                        )}
                      >
                        <span className='hidden 2xl:inline'>
                          {row.dte_type_description || '-'}{stateLabel ? ` ${stateLabel}` : ''}
                        </span>
                        <span className='inline 2xl:hidden'>
                          {getDteAbbreviation(code)}{stateLabel ? ` ${stateLabel}` : ''}
                        </span>
                      </span>
                    </td>
                  )
                }

                if (field === 'date_issued') {
                  return (
                    <td key={cIdx} className={styles.td}>
                      <span className={styles.boldText}>
                        {row.date_issued ? formatDate(row.date_issued) : '-'}
                      </span>
                    </td>
                  )
                }

                if (field === 'direction_label') {
                  const isEnviado = row.__document_type === 'issued'
                  return (
                    <td key={cIdx} className={styles.td}>
                      <span
                        className={clsx(
                          'px-2 py-1 rounded-full text-[9px] font-bold border',
                          isEnviado
                            ? 'bg-blue-50 text-blue-600 border-blue-100'
                            : 'bg-teal-50 text-teal-600 border-teal-100'
                        )}
                      >
                        {row.direction_label}
                      </span>
                    </td>
                  )
                }

                if (field === 'amount_with_iva') {
                  const { kind, total, neto, iva, exento } = calcAmountsForRow(row)

                  return (
                    <td key={cIdx} className={styles.td}>
                      <div className='flex flex-col items-end justify-center leading-tight sm:pr-4'>
                        <span className={clsx(styles.boldText, 'font-bold pl-2')}>
                          {formatCLP(total)}
                        </span>

                        <span
                          className={clsx(
                            'text-[0.6rem] sm:text-[0.65rem]',
                            kind === 'afecto' ? 'text-red-500' : 'text-red-500/70'
                          )}
                        >
                          IVA:{kind === 'afecto' ? ` ${formatCLP(iva)}` : ''}
                        </span>

                        {kind === 'exento' ? (
                          <span className='text-[0.6rem] sm:text-[0.65rem] text-gray-500'>
                            Exento: {formatCLP(exento)}
                          </span>
                        ) : (
                          <span className='text-[0.6rem] sm:text-[0.65rem] text-gray-500'>
                            Neto: {formatCLP(neto)}
                          </span>
                        )}
                      </div>
                    </td>
                  )
                }

                if (field === 'estado_ui') {
                  return (
                    <td key={cIdx} className={styles.td}>
                      <button
                        type='button'
                        onClick={() => handleOpenStateModal(row)}
                        className='font-semibold text-[0.65rem] sm:text-[0.705rem] px-3 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors'
                      >
                        {row.estado_ui}
                      </button>
                    </td>
                  )
                }

                if (field === 'archivo') {
                  const iconBtn =
                    'inline-flex items-center justify-center transition-transform duration-150 ease-out hover:scale-110 active:scale-95'

                  const key = getRowKey?.(row)
                  const detail = key ? detailsByKey?.[key] : null

                  const activeActions = key ? loadingActionByKey?.[key] : null
                  const isDocLoading = detail?.status === 'loading'
                  const showPdfSpinner = (isDocLoading || activeActions?.pdf) && activeActions?.pdf
                  const showXmlSpinner = (isDocLoading || activeActions?.xml) && activeActions?.xml

                  const disablePdf = (isDocLoading && activeActions?.pdf) || !!activeActions?.pdf
                  const disableXml = (isDocLoading && activeActions?.xml) || !!activeActions?.xml

                  return (
                    <td key={cIdx} className={styles.td}>
                      <div className='flex items-center justify-center gap-2'>
                        <button
                          type='button'
                          onClick={() => onOpenPdf?.(row)}
                          disabled={disablePdf}
                          className={clsx(iconBtn, 'text-red-500 hover:text-red-700', {
                            'opacity-40 cursor-not-allowed': disablePdf
                          })}
                          title='Ver PDF'
                        >
                          {showPdfSpinner ? (
                            <Spinner />
                          ) : (
                            <PictureAsPdfOutlinedIcon fontSize='small' />
                          )}
                        </button>

                        <button
                          type='button'
                          onClick={() => onOpenXml?.(row)}
                          disabled={disableXml}
                          className={clsx(iconBtn, 'text-green-500 hover:text-green-700', {
                            'opacity-40 cursor-not-allowed': disableXml
                          })}
                          title='Ver XML'
                        >
                          {showXmlSpinner ? (
                            <Spinner />
                          ) : (
                            <DescriptionOutlinedIcon fontSize='small' />
                          )}
                        </button>
                      </div>
                    </td>
                  )
                }

                if (field === 'estado_ui') {
                  return (
                    <td key={cIdx} className={styles.td}>
                      <button
                        type='button'
                        onClick={() => handleOpenStateModal(row)}
                        className='font-semibold text-[0.65rem] sm:text-[0.705rem] px-3 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors'
                      >
                        {row.estado_ui}
                      </button>
                    </td>
                  )
                }

                if (field === 'acciones') {
                  const isIssued = row.__document_type === 'issued'
                  const code = String(row?.dte_type_code ?? row?.dte_type?.code)
                  const isNC = code === '61'
                  const canEmitNC = isIssued && !isNC
                  const isFactura = code === '33' || code === '34'
                  const canClone = isIssued && isFactura

                  return (
                    <td key={cIdx} className={styles.td}>
                      <div className='flex items-center justify-center gap-4'>
                        {/* Botón: Emitir NC */}
                        {canEmitNC ? (
                          <div className='flex flex-col items-center gap-1'>
                            <button
                              type='button'
                              onClick={() => onEmitNC?.(row)}
                              className='flex items-center justify-center w-8 h-8 rounded-full shadow-sm transition-transform hover:scale-110 active:scale-95 border border-gray-400 bg-white text-slate-400'
                              title='Emitir Nota de Crédito'
                            >
                              <img src={iconEnlace} alt='Emitir NC' className='w-5 h-5' />
                            </button>
                            <span className='text-[10px] text-gray-500 font-bold'>Emitir NC</span>
                          </div>
                        ) : (
                          <div className='flex flex-col items-center gap-1 opacity-50'>
                            <button
                              type='button'
                              disabled
                              className='flex items-center justify-center w-8 h-8 rounded-full shadow-sm border border-gray-400 bg-gray-50 text-slate-400 cursor-not-allowed'
                              title='No disponible para este tipo de documento'
                            >
                              <img
                                src={iconEnlace}
                                alt='Emisión no disponible'
                                className='w-5 h-5 grayscale'
                              />
                            </button>
                            <span className='text-[10px] text-gray-400 font-bold cursor-not-allowed'>
                              Emitir NC
                            </span>
                          </div>
                        )}

                        {/* Botón: Clonar Factura */}
                        {canClone ? (
                          <div className='flex flex-col items-center gap-1'>
                            <button
                              type='button'
                              onClick={() => onCloneDoc?.(row)}
                              className='flex items-center justify-center w-8 h-8 rounded-full shadow-sm transition-transform hover:scale-110 active:scale-95 border border-gray-400 bg-white text-slate-400'
                              title='Copiar Documento'
                            >
                              <img src={coparDoc} alt='Copiar Documento' className='w-5 h-5' />
                            </button>
                            <span className='text-[10px] text-gray-500 font-bold'>Copiar Doc.</span>
                          </div>
                        ) : (
                          <div className='flex flex-col items-center gap-1 opacity-50'>
                            <button
                              type='button'
                              disabled
                              className='flex items-center justify-center w-8 h-8 rounded-full shadow-sm border border-gray-400 bg-gray-50 text-slate-400 cursor-not-allowed'
                              title='No disponible para copiar documento'
                            >
                              <img
                                src={coparDoc}
                                alt='Edición no disponible'
                                className='w-5 h-5 grayscale'
                              />
                            </button>
                            <span className='text-[10px] text-gray-400 font-bold cursor-not-allowed'>
                              Copiar Doc.
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  )
                }

                if (field === 'receiver_name') {
                  return (
                    <td key={cIdx} className={styles.td}>
                      <div className='flex flex-col items-center justify-center'>
                        <span className={styles.boldText}>{row.receiver_name || '-'}</span>
                        <span className={styles.text}>{row.receiver_tax_id || ''}</span>
                      </div>
                    </td>
                  )
                }

                if (field === 'sender_name') {
                  return (
                    <td key={cIdx} className={styles.td}>
                      <div className='flex flex-col items-center justify-center'>
                        <span className={styles.boldText}>{row.sender_name || '-'}</span>
                        <span className={styles.text}>{row.sender_tax_id || ''}</span>
                      </div>
                    </td>
                  )
                }

                return (
                  <td key={cIdx} className={styles.td}>
                    <span className={styles.text}>{row[field]}</span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div ref={lastElementRef} className='flex justify-center py-4 flex-shrink-0'>
        {isFetchingNextPage && (
          <Lottie animationData={LoaderAdm} loop style={{ width: 60, height: 60 }} />
        )}
        {!hasNextPage && rows.length > 0 && (
          <div className='text-gray-400 text-[10px] py-2'>No hay más resultados</div>
        )}
      </div>

      <EstadoTrazaModal
        open={stateModalOpen}
        onClose={handleCloseStateModal}
        data={selectedRowState}
      />
    </div>
  )
}
